/**
 * GUPM-329 — histórico de leituras de um instrumento (seção "Histórico" do M6).
 *
 * Contrato GENÉRICO: cada tipo de instrumento declara as suas colunas e como uma
 * leitura (ou um grupo delas) vira linha. A tela só desenha o que vier daqui. Pedido
 * do dono em 28/09/2026: todo instrumento vai ter a sua tabela de série histórica,
 * mas por enquanto só o marco superficial declara a dele — os demais tipos devolvem
 * `null` e a seção não aparece.
 *
 * Domínio puro: sem React, sem Supabase, sem DOM.
 */
import type { Instrument, Reading } from '..';
import { diaEmBrasilia, formatarDiaBr } from '../fuso-brasilia';
import { deslocamentoDoMarco, type PosicaoMarco } from './deslocamento-marco';
import { ehMarco } from './linhas-de-marco';

export interface ColunaHistorico {
  /** Chave em `LinhaHistorico.valores`. */
  id: string;
  rotulo: string;
  unidade: string;
  /** Casas decimais de exibição — a resolução da medição, não um gosto da tela. */
  casas: number;
  /** O número que se acompanha (ex.: o deslocamento do marco); o resto é contexto. */
  destaque?: boolean;
}

export interface LinhaHistorico {
  ts: Date;
  /** Data no fuso da UPM; numera medições do mesmo dia em vez de exibir hora. */
  rotuloData: string;
  /** `null` = não medido / não calculável. Nunca zero no lugar de ausência. */
  valores: Record<string, number | null>;
}

export interface HistoricoDoInstrumento {
  colunas: readonly ColunaHistorico[];
  /** Como se chama uma linha deste tipo: "campanha" no marco, "leitura" no automático. */
  nomeDaLinha: { singular: string; plural: string };
  /** Mais recente primeiro. */
  linhas: LinhaHistorico[];
}

/**
 * Colunas do marco. Posição em METROS com 3 casas (o milímetro — mesma resolução da
 * tabela de Marcos Superficiais do Resumo Técnico); deslocamento em MILÍMETROS com
 * 1 casa (0,1 mm, a resolução da planilha).
 */
const COLUNAS_MARCO: readonly ColunaHistorico[] = [
  { id: 'easting', rotulo: 'E', unidade: 'm', casas: 3 },
  { id: 'northing', rotulo: 'N', unidade: 'm', casas: 3 },
  { id: 'cota', rotulo: 'Cota', unidade: 'm', casas: 3 },
  { id: 'deltaLesteMm', rotulo: 'ΔE', unidade: 'mm', casas: 1, destaque: true },
  { id: 'deltaNorteMm', rotulo: 'ΔN', unidade: 'mm', casas: 1, destaque: true },
  { id: 'recalqueMm', rotulo: 'Recalque', unidade: 'mm', casas: 1, destaque: true },
];

/** O instrumento tem seção "Histórico"? Hoje só o marco superficial. */
export function temHistorico(type: string): boolean {
  return ehMarco(type);
}

/**
 * Histórico completo do instrumento a partir das suas leituras (de qualquer canal).
 * `null` para tipo que ainda não declarou colunas.
 */
export function historicoDoInstrumento(
  instrument: Instrument,
  leituras: readonly Reading[],
): HistoricoDoInstrumento | null {
  if (!temHistorico(instrument.type)) return null;
  return historicoDoMarco(instrument, leituras.filter((r) => r.instrumentId === instrument.id));
}

/**
 * Marco: as três leituras de uma campanha (easting, northing, vertical) compartilham
 * o mesmo instante — é assim que a carga as grava — e viram UMA linha. O Δ usa
 * `deslocamentoDoMarco`, a mesma função da tabela do Resumo Técnico: as duas não
 * podem discordar sobre o mesmo marco.
 */
function historicoDoMarco(instrument: Instrument, leituras: readonly Reading[]): HistoricoDoInstrumento {
  const referencia: PosicaoMarco | null =
    instrument.easting === null && instrument.northing === null && instrument.cotaTopo === null
      ? null
      : { easting: instrument.easting, northing: instrument.northing, cota: instrument.cotaTopo };

  const porInstante = new Map<number, PosicaoMarco>();
  for (const r of leituras) {
    const t = r.ts.getTime();
    const pos = porInstante.get(t) ?? { easting: null, northing: null, cota: null };
    if (r.canal === 'easting') pos.easting = r.value;
    else if (r.canal === 'northing') pos.northing = r.value;
    else if (r.canal === 'vertical') pos.cota = r.value;
    porInstante.set(t, pos);
  }

  const instantes = [...porInstante.keys()].sort((a, b) => b - a);
  const rotulos = rotulosDeData(instantes);
  const linhas = instantes.map((t, i): LinhaHistorico => {
    const pos = porInstante.get(t)!;
    const delta = deslocamentoDoMarco(referencia, pos);
    return {
      ts: new Date(t),
      rotuloData: rotulos[i]!,
      valores: {
        easting: pos.easting,
        northing: pos.northing,
        cota: pos.cota,
        deltaLesteMm: delta.deltaLesteMm,
        deltaNorteMm: delta.deltaNorteMm,
        recalqueMm: delta.recalqueMm,
      },
    };
  });

  return { colunas: COLUNAS_MARCO, nomeDaLinha: { singular: 'campanha', plural: 'campanhas' }, linhas };
}

/**
 * Rótulo de data de cada instante (ordem decrescente). Duas medições no mesmo dia —
 * reais, na Mina — ganham "1ª/2ª medição" em vez da hora: a hora gravada (08:00 e
 * 14:00) é convenção da carga, e exibi-la afirmaria um horário que ninguém anotou.
 */
function rotulosDeData(instantesDesc: readonly number[]): string[] {
  const dias = instantesDesc.map((t) => diaEmBrasilia(new Date(t)));
  const total = new Map<string, number>();
  for (const d of dias) total.set(d, (total.get(d) ?? 0) + 1);

  // Ordem decrescente: a 1ª medição do dia é a ÚLTIMA a aparecer.
  const vistos = new Map<string, number>();
  return dias.map((d) => {
    const n = total.get(d)!;
    const base = formatarDiaBr(d);
    if (n === 1) return base;
    const jaVistos = vistos.get(d) ?? 0;
    vistos.set(d, jaVistos + 1);
    return `${base} · ${n - jaVistos}ª medição`;
  });
}
